import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { captureEvent } from "./analytics";
import { useAuth } from "./auth/useAuth";
import { Disclaimer } from "./components/Disclaimer";
import { ErrorState } from "./components/ErrorState";
import { FreePropertyView } from "./components/FreePropertyView";
import { LoadingState } from "./components/LoadingState";
import { NavBar } from "./components/NavBar";
import { PaidPropertyView } from "./components/PaidPropertyView";
import { ScoreForm } from "./components/ScoreForm";
import { getScore, getUsage, postAutocomplete, postScore, resolvePlace } from "./api/client";
import type { ScoreResponse } from "./types/api";

/**
 * App — tier-aware routing (Req 1, 2, 20; Task 9.0 chat-forward free tier).
 *
 * Rendered only for authenticated users (wrapped in <RequireAuth> in main.tsx;
 * feature facebook-login-branded-auth Req 8) — there is no in-app sign-in view.
 * Two render modes:
 *   - Free tier → ScoreForm → FreePropertyView (At-A-Glance + chat-forward +
 *                 Lock_Panels). No monolithic report.
 *   - Paid tier → ScoreForm → ScoreResult (verified Tilt Map + chat)
 *
 * Tier comes from `useAuth().tier` (Cognito ID-token `custom:tier`). The
 * Stripe upgrade flow + Pattern C polling lives on the /upgrade page
 * (Task 9.3); handleUpgradeClick routes there.
 */

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  // Paid score in flight — we render the Pro shell (At-A-Glance + map +
  // summary card) immediately and skeleton the verified panels, instead of a
  // blank "scoring…" wait right after the user paid.
  | {
      kind: "scoring";
      placeId: string | null;
      address: string;
      askingPrice: number;
    }
  | { kind: "error"; message: string; code: string }
  | {
      kind: "scored";
      result: ScoreResponse;
      placeId: string | null;
      address: string;
      askingPrice: number;
    };

// The free-tier report context is persisted to sessionStorage so it survives
// navigating to /upgrade (a separate route that unmounts App) and back, plus
// reloads and the Stripe round-trip — users shouldn't lose their report when
// they go look at the Pro page. (Sets up "save the report if they upgrade".)
const PROPERTY_CONTEXT_KEY = "pt.propertyContext";

type PropertyContext = {
  address: string;
  askingPrice: number | null;
  placeId: string;
  lat: number | null;
  lon: number | null;
};

function loadPropertyContext(): PropertyContext | null {
  try {
    const raw = sessionStorage.getItem(PROPERTY_CONTEXT_KEY);
    return raw ? (JSON.parse(raw) as PropertyContext) : null;
  } catch {
    return null;
  }
}

export function App() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  // Free-tier chat-forward context (Task 9.0): the property the user is
  // asking about. Needs a place_id to target the chat. Rehydrated from
  // sessionStorage so it survives the trip to /upgrade and back.
  const [propertyContext, setPropertyContext] = useState<PropertyContext | null>(
    loadPropertyContext,
  );
  // Pattern C (Req 20.5): carry the preserved property into a Pro score right
  // after upgrade. `prefill` seeds the paid form when we have the property but
  // no price (free price is optional). `upgradeHandled` makes it fire once.
  const [prefill, setPrefill] = useState<{
    address: string;
    placeId: string;
    fromUpgrade?: boolean;
  } | null>(null);
  const upgradeHandled = useRef(false);
  // A saved property the user clicked to "view report" (read on mount from
  // sessionStorage); acted on once auth is confirmed paid.
  const [pendingSaved, setPendingSaved] = useState<{
    address: string;
    placeId: string;
    askingPrice: number | null;
  } | null>(null);
  const savedHandled = useRef(false);

  // usage-tracking-limits Req 11 — free-tier "Try PriceTilt Pro" trial. When a
  // free user spends their one report, `trialScore` holds the unlocked Pro
  // report (rendered via PaidPropertyView, the same as a paid score). If the
  // trial is already spent, the free view swaps the CTA for upgrade messaging.
  const [trialScore, setTrialScore] = useState<ScoreResponse | null>(null);
  const [tryProPending, setTryProPending] = useState(false);
  const [trialSpent, setTrialSpent] = useState(false);
  const [tryProError, setTryProError] = useState<string | null>(null);
  // Which property the free user unlocked with their trial (place_id + the
  // stored report's score_id), from GET /usage. Drives permanent per-property
  // Pro access (Req 3.5/11.4): re-opening that property renders the full stored
  // Pro report, not the free view.
  const [proTrialUnlock, setProTrialUnlock] = useState<{
    placeId: string;
    scoreId: string;
  } | null>(null);

  // The unlocked report itself, fetched eagerly (stored data — no ATTOM/quota)
  // so "View your unlocked Pro report" works instantly and we can show its
  // address even on the blank search screen.
  const [unlockedReport, setUnlockedReport] = useState<ScoreResponse | null>(null);
  // Why the trial is unavailable when this account never spent one — e.g.
  // "email_already_used" (grant belongs to another/deleted account, Req 7.2).
  const [trialReason, setTrialReason] = useState<string | null>(null);

  function applyUsage(u: {
    pro_trial_remaining?: number;
    pro_trial_spent_on?: string | null;
    pro_trial_score_id?: string | null;
    trial_unavailable_reason?: string;
  }) {
    if ((u.pro_trial_remaining ?? 1) <= 0) setTrialSpent(true);
    if (u.trial_unavailable_reason) setTrialReason(u.trial_unavailable_reason);
    if (u.pro_trial_spent_on && u.pro_trial_score_id) {
      setProTrialUnlock({
        placeId: u.pro_trial_spent_on,
        scoreId: u.pro_trial_score_id,
      });
    }
  }

  // Proactively learn the trial state on load: spent → upgrade CTA (never a
  // "Try Pro" button the gate would deny); unlocked property → fetchable
  // report. Falls back to the click-time 403 if this read fails.
  useEffect(() => {
    if (auth.tier !== "free") return;
    let cancelled = false;
    getUsage()
      .then((u) => {
        if (!cancelled) applyUsage(u);
      })
      .catch(() => {
        /* non-critical — the Try Pro click still 403s and flips the CTA */
      });
    return () => {
      cancelled = true;
    };
    // applyUsage is a stable hoisted declaration; intentionally not a dep.
  }, [auth.tier]);

  // Fetch the unlocked report once we know its score_id (permanent per-property
  // unlock, Req 3.5/11.4 — served from stored data, no new ATTOM, no quota).
  useEffect(() => {
    if (auth.tier !== "free" || !proTrialUnlock || unlockedReport) return;
    let cancelled = false;
    getScore(proTrialUnlock.scoreId)
      .then((r) => {
        if (!cancelled) setUnlockedReport(r);
      })
      .catch(() => {
        /* the spent card just won't offer the View button */
      });
    return () => {
      cancelled = true;
    };
  }, [auth.tier, proTrialUnlock, unlockedReport]);

  // Explicit "View your unlocked Pro report" action — deterministic; does not
  // depend on sessionStorage or the user re-searching the same place_id.
  function handleViewUnlocked() {
    if (!unlockedReport) return;
    setPropertyContext({
      address: unlockedReport.address,
      askingPrice: unlockedReport.asking_price,
      placeId: unlockedReport.place_id,
      lat: unlockedReport.lat,
      lon: unlockedReport.lon,
    });
    setTrialScore(unlockedReport);
    setStatus({ kind: "idle" });
  }

  // Account page → "View your Pro report" handoff: AccountPage sets a
  // sessionStorage flag and routes home; open the unlocked report once it's
  // loaded here.
  useEffect(() => {
    if (auth.tier !== "free" || !unlockedReport) return;
    let flagged = false;
    try {
      flagged = sessionStorage.getItem("pt.viewUnlockedReport") === "1";
      if (flagged) sessionStorage.removeItem("pt.viewUnlockedReport");
    } catch {
      flagged = false;
    }
    if (flagged) handleViewUnlocked();
    // handleViewUnlocked is a stable hoisted declaration; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.tier, unlockedReport]);

  // Auto-render the unlocked report when the free user navigates to its
  // property; drop back to the free view on any other property.
  useEffect(() => {
    if (auth.tier !== "free" || !propertyContext || !proTrialUnlock) return;
    if (propertyContext.placeId === proTrialUnlock.placeId) {
      if (unlockedReport && (!trialScore || trialScore.score_id !== proTrialUnlock.scoreId)) {
        setTrialScore(unlockedReport);
      }
    } else if (trialScore && trialScore.score_id === proTrialUnlock.scoreId) {
      setTrialScore(null);
    }
  }, [auth.tier, propertyContext, proTrialUnlock, trialScore, unlockedReport]);

  async function handleTryPro() {
    if (!propertyContext || propertyContext.askingPrice == null) return;
    setTryProPending(true);
    setTryProError(null);
    captureEvent("report_requested", { tier: "free", used_pro_trial: true });
    try {
      const result = await postScore(
        propertyContext.address,
        propertyContext.askingPrice,
        { place_id: propertyContext.placeId, is_trial_action: true },
      );
      setTrialScore(result);
      setTrialSpent(true);
      captureEvent("report_viewed", {
        tier: "free",
        context: "trial",
        quadrant: result.tilt_map?.quadrant,
      });
      // Record the unlock so returning to this property re-renders the Pro
      // report (and so the render effect doesn't clear it as "not unlocked").
      if (propertyContext.placeId) {
        setProTrialUnlock({ placeId: propertyContext.placeId, scoreId: result.score_id });
      }
    } catch (e: unknown) {
      const err = e as { status?: number; code?: string; message?: string };
      // Trial already spent (or email-deduped) → flip to upgrade messaging AND
      // refetch usage so the unlocked report (if this account has one) becomes
      // viewable rather than the click silently dead-ending.
      if (err.code === "trial_exhausted") {
        setTrialSpent(true);
        captureEvent("paywall_hit", { reason: "pro_trial_exhausted" });
        getUsage().then(applyUsage).catch(() => {});
      }
      setTryProError(
        err.message || "Couldn't generate your Pro report. Please try again.",
      );
    } finally {
      setTryProPending(false);
    }
  }

  useEffect(() => {
    try {
      if (propertyContext) {
        sessionStorage.setItem(PROPERTY_CONTEXT_KEY, JSON.stringify(propertyContext));
      } else {
        sessionStorage.removeItem(PROPERTY_CONTEXT_KEY);
      }
    } catch {
      // sessionStorage unavailable (private mode / quota) — degrade silently.
    }
  }, [propertyContext]);

  // Pattern C — when a free user returns from a successful upgrade, score the
  // property they were viewing (or prefill it) instead of a blank form.
  useEffect(() => {
    if (upgradeHandled.current) return;
    if (auth.tier !== "paid" || !propertyContext) return;
    let justUpgraded = false;
    try {
      justUpgraded = sessionStorage.getItem("pt.justUpgraded") === "1";
    } catch {
      justUpgraded = false;
    }
    if (!justUpgraded) return;
    upgradeHandled.current = true;
    try {
      sessionStorage.removeItem("pt.justUpgraded");
    } catch {
      /* ignore */
    }
    if (propertyContext.askingPrice != null) {
      void handleSubmit(propertyContext.address, propertyContext.askingPrice, propertyContext.placeId);
    } else {
      // No price (free price is optional) — seed the form so they add one click.
      setPrefill({
        address: propertyContext.address,
        placeId: propertyContext.placeId,
        fromUpgrade: true,
      });
    }
    // handleSubmit is a stable hoisted declaration; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.tier, propertyContext]);

  // Saved-property "view report" (Saved-A): the Saved page stashes a property
  // and routes home. Read it on mount (auth may still be loading here).
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem("pt.prefillProperty");
    } catch {
      raw = null;
    }
    if (!raw) return;
    try {
      sessionStorage.removeItem("pt.prefillProperty");
    } catch {
      /* ignore */
    }
    try {
      const p = JSON.parse(raw);
      if (p?.address && p?.placeId) {
        setPendingSaved({
          address: p.address,
          placeId: p.placeId,
          askingPrice: typeof p.askingPrice === "number" ? p.askingPrice : null,
        });
      }
    } catch {
      /* ignore malformed */
    }
  }, []);

  // Act on the clicked saved property once auth is confirmed paid: re-score
  // directly when we saved a price, else prefill the form to add one.
  useEffect(() => {
    if (savedHandled.current || !pendingSaved) return;
    if (auth.tier !== "paid") return;
    savedHandled.current = true;
    if (pendingSaved.askingPrice != null) {
      void handleSubmit(pendingSaved.address, pendingSaved.askingPrice, pendingSaved.placeId);
    } else {
      setPrefill({ address: pendingSaved.address, placeId: pendingSaved.placeId });
    }
    // handleSubmit is a stable hoisted declaration; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.tier, pendingSaved]);

  async function handleSubmit(
    address: string,
    askingPrice: number | null,
    placeId: string | null,
  ) {
    captureEvent("report_requested", {
      tier: auth.tier === "paid" ? "pro" : "free",
      used_pro_trial: false,
    });
    if (auth.tier === "free") {
      // Free tier is chat-forward (Task 9.0): resolve a place_id (the chat
      // targets the property by place_id, not a score). If the user typed
      // the address without picking an autocomplete suggestion, fall back
      // to resolving the first match so the chat still has a target.
      setStatus({ kind: "loading" });
      try {
        let resolved = placeId;
        if (!resolved) {
          const ac = await postAutocomplete(address);
          resolved = ac.suggestions[0]?.place_id ?? null;
        }
        if (!resolved) {
          setStatus({
            kind: "error",
            code: "address_not_found",
            message: "Couldn't find that address. Try selecting from the suggestions.",
          });
          return;
        }
        // Resolve coordinates for the At-A-Glance map (Task 9.2). The map is
        // non-essential to the chat, so a coord-resolution failure degrades
        // to a map placeholder rather than blocking the whole free view.
        let lat: number | null = null;
        let lon: number | null = null;
        try {
          const place = await resolvePlace(resolved);
          lat = place.lat;
          lon = place.lon;
        } catch {
          // Map will render its "unavailable" placeholder.
        }
        setPropertyContext({ address, askingPrice, placeId: resolved, lat, lon });
        setStatus({ kind: "idle" });
      } catch (e: unknown) {
        const err = e as { code?: string; message?: string };
        setStatus({
          kind: "error",
          code: err.code || "unknown",
          message: err.message || "Couldn't resolve that address.",
        });
      }
      return;
    }
    // Paid tier needs a price for the offer-range math (Req 11.4). The
    // score itself doesn't use price (Req 11.3), but the offer range can't
    // render without it — so prompt for one rather than scoring blind.
    if (askingPrice === null) {
      setStatus({
        kind: "error",
        code: "price_required",
        message: "Enter an asking price so we can show your offer range.",
      });
      return;
    }
    // Render the Pro shell immediately (At-A-Glance + map + summary card),
    // skeleton the verified panels, and fill them when /score returns.
    setStatus({ kind: "scoring", placeId, address, askingPrice });
    try {
      const result = await postScore(address, askingPrice, placeId);
      setStatus({ kind: "scored", result, placeId, address, askingPrice });
      captureEvent("report_viewed", {
        tier: "pro",
        context: "pro",
        quadrant: result.tilt_map?.quadrant,
      });
    } catch (e: unknown) {
      const err = e as { code?: string; message?: string };
      setStatus({
        kind: "error",
        code: err.code || "unknown",
        message: err.message || "Something went wrong.",
      });
    }
  }

  function handleUpgradeClick() {
    // Client-side nav (not window.location) so we don't hard-reload the SPA.
    // The report is in sessionStorage, so it's preserved across the trip to
    // /upgrade and back via the "Back to PriceTilt Report" link.
    navigate("/upgrade");
  }

  // -- Render modes --------------------------------------------------------
  // App is always rendered inside <RequireAuth> (main.tsx), so by the time it
  // mounts the session is settled and authenticated — the old auth.loading
  // guard and the "Sign in to continue" splash page are gone (feature
  // facebook-login-branded-auth, Req 8.1, 8.2). Unauthenticated visitors are
  // redirected to Managed Login upstream; marketing owns all pre-auth messaging.

  // --- Free tier — chat-forward (Task 9.0) -------------------------------

  if (auth.tier === "free") {
    if (status.kind === "loading")
      return (
        <LoadingState
          message="Pulling property data and scoring…"
          detail="This typically takes 15–30 seconds."
        />
      );
    if (!propertyContext) {
      return (
        <div className="app">
          <NavBar />
          <main>
            {status.kind === "error" && (
              <ErrorState
                code={status.code}
                message={status.message}
                onRetry={() => setStatus({ kind: "idle" })}
              />
            )}
            <ScoreForm onSubmit={handleSubmit} />
            <Disclaimer />
          </main>
        </div>
      );
    }
    return (
      <div className="app">
        <NavBar />
        <main>
          {/* PaidPropertyView renders its own "New search" — only add ours on
              the free view so the unlocked report doesn't show two. */}
          {!trialScore && (
            <div className="new-search-row">
              <button
                type="button"
                className="new-search"
                onClick={() => {
                  setPropertyContext(null);
                  setStatus({ kind: "idle" });
                  setTrialScore(null);
                  setTryProError(null);
                }}
              >
                ← New search
              </button>
            </div>
          )}
          {trialScore ? (
            // Trial spent on this property — render the full Pro report, exactly
            // as a paid score (Req 11.4: permanently unlocked for this user).
            <PaidPropertyView
              result={trialScore}
              placeId={propertyContext.placeId}
              address={propertyContext.address}
              askingPrice={propertyContext.askingPrice ?? 0}
              onNewSearch={() => {
                setPropertyContext(null);
                setStatus({ kind: "idle" });
                setTrialScore(null);
              }}
            />
          ) : (
            <FreePropertyView
              address={propertyContext.address}
              placeId={propertyContext.placeId}
              askingPrice={propertyContext.askingPrice}
              lat={propertyContext.lat}
              lon={propertyContext.lon}
              onUpgradeClick={handleUpgradeClick}
              onTryPro={handleTryPro}
              tryProPending={tryProPending}
              trialSpent={trialSpent}
              tryProError={tryProError}
              trialSpentReason={trialReason}
              onViewUnlocked={unlockedReport ? handleViewUnlocked : undefined}
              unlockedAddress={unlockedReport?.address ?? null}
            />
          )}
        </main>
      </div>
    );
  }

  // --- Paid tier — verified Tilt Map + chat ----------------------------

  return (
    <div className="app">
      <NavBar />

      <main>
        {status.kind === "idle" && (
          <>
            {prefill && (
              <p className="upgrade-prefill-hint" role="status">
                {prefill.fromUpgrade ? (
                  <>
                    You&rsquo;re on PriceTilt Pro 🎉 — add an asking price to see
                    the verified Tilt Map for <strong>{prefill.address}</strong>.
                  </>
                ) : (
                  <>
                    Add an asking price to re-score{" "}
                    <strong>{prefill.address}</strong>.
                  </>
                )}
              </p>
            )}
            <ScoreForm
              onSubmit={handleSubmit}
              priceRequired={auth.tier === "paid"}
              initialAddress={prefill?.address}
              initialPlaceId={prefill?.placeId}
            />
          </>
        )}
        {status.kind === "loading" && (
          <LoadingState
            message="Pulling property data and scoring…"
            detail="This typically takes 15–30 seconds."
          />
        )}
        {status.kind === "error" && (
          <ErrorState
            code={status.code}
            message={status.message}
            onRetry={() => setStatus({ kind: "idle" })}
          />
        )}
        {status.kind === "scoring" && (
          <PaidPropertyView
            result={null}
            placeId={status.placeId}
            address={status.address}
            askingPrice={status.askingPrice}
            onNewSearch={() => setStatus({ kind: "idle" })}
          />
        )}
        {status.kind === "scored" && (
          <PaidPropertyView
            result={status.result}
            placeId={status.placeId}
            address={status.address}
            askingPrice={status.askingPrice}
            onNewSearch={() => setStatus({ kind: "idle" })}
          />
        )}
        {status.kind !== "scored" && status.kind !== "scoring" && <Disclaimer />}
      </main>
    </div>
  );
}
